// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/lib/auth/useAuth'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { MicrosoftSignInButton } from '@/components/auth/MicrosoftSignInButton'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { ROUTES } from '@/constants/routes'
import { friendlyError } from '@/lib/utils/errorHandler'

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? ROUTES.DASHBOARD

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const msErr = searchParams.get('microsoft_error')
    if (msErr) {
      setError(msErr)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mfaToken) {
        const result = await login({ mfaToken, mfaCode })
        if ('ok' in result) navigate(from, { replace: true })
        return
      }

      const result = await login({ email, password })
      if ('mfaRequired' in result) {
        setMfaToken(result.mfaToken)
        setMfaCode('')
        return
      }
      if ('ok' in result) navigate(from, { replace: true })
    } catch (err) {
      setError(friendlyError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className="mb-8 text-center">
        <h1 className="text-xl font-bold text-ink">Sign in to Etapsky Portal</h1>
        <p className="mt-1 text-sm text-ink-muted">Manage your SDF documents and API keys</p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="card p-6 space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {mfaToken ? (
          <>
            <p className="text-sm text-ink-muted">
              Enter the 6-digit code from your authenticator app, or a 10-character recovery code.
            </p>
            <Input
              label="Authenticator or recovery code"
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder="123456 or recovery code"
              required
              autoComplete="one-time-code"
              autoFocus
            />
            <Button type="submit" variant="primary" className="w-full" loading={loading}>
              Verify and sign in
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-xs"
              onClick={() => {
                setMfaToken(null)
                setMfaCode('')
                setError('')
              }}
            >
              Back to password
            </Button>
          </>
        ) : (
          <>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />

            <div className="flex items-center justify-between">
              <Link to={ROUTES.FORGOT_PASSWORD} className="text-xs text-brand hover:underline">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" variant="primary" className="w-full" loading={loading}>
              Sign in
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center" aria-hidden>
                <span className="w-full border-t border-line" />
              </div>
              <div className="relative flex justify-center text-xs font-medium">
                <span className="bg-[var(--raised)] px-3 text-ink-muted">or</span>
              </div>
            </div>

            <MicrosoftSignInButton disabled={loading} onError={setError} />
          </>
        )}
      </form>

      <p className="mt-4 text-center text-sm text-ink-muted">
        Don't have an account?{' '}
        <Link to={ROUTES.REGISTER} className="text-brand hover:underline font-medium">
          Request access
        </Link>
      </p>
    </AuthLayout>
  )
}
